import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
    ShoppingBag,
    Menu,
    X,
    Store,
    LogOut,
    Shield,
    User as UserIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/lib/auth";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Secciones de la landing. Son anclas, no rutas.
const SECTIONS = [
    ["como-funciona", "Cómo funciona"],
    ["beneficios", "Beneficios"],
    ["precios", "Precios"],
    ["faq", "Preguntas"],
];

// Rutas donde no hay nada que comprar: el carrito solo aparece si trae algo.
const MARKETING_PATHS = new Set([
    "/", "/login", "/registro", "/terminos", "/privacidad",
]);

export default function Navbar({ cart }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { user, isAuthenticated, logout } = useAuth();

    const showCart = cart.count > 0 || !MARKETING_PATHS.has(pathname);

    const goToSection = (id) => (e) => {
        e.preventDefault();
        setMobileOpen(false);
        if (pathname === "/") {
            const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
            document.getElementById(id)?.scrollIntoView({
                behavior: reduce ? "auto" : "smooth",
                block: "start",
            });
        } else {
            navigate(`/#${id}`);
        }
    };

    return (
        <header
            data-testid="navbar"
            className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-xl border-b border-zinc-200"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="h-[72px] flex items-center gap-6">
                    {/* Logo */}
                    <Link
                        to="/"
                        data-testid="navbar-logo"
                        aria-label="Tiendify — Inicio"
                        className="flex items-center gap-2 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 rounded-md"
                    >
                        <img
                            src="/assets/images/t-mark.png"
                            alt=""
                            width="32"
                            height="32"
                            className="w-8 h-8 object-contain"
                        />
                        <img
                            src="/assets/images/logo-text.png"
                            alt="Tiendify"
                            height="20"
                            className="h-5 w-auto object-contain"
                        />
                    </Link>

                    {/* Secciones (solo desktop) */}
                    <nav
                        className="hidden lg:flex items-center gap-1 text-[15px]"
                        aria-label="Navegación principal"
                    >
                        {SECTIONS.map(([id, label]) => (
                            <a
                                key={id}
                                href={`/#${id}`}
                                onClick={goToSection(id)}
                                data-testid={`navbar-link-${id}`}
                                className="px-3 py-2 font-medium text-[#1A1A1A] rounded-md hover:bg-zinc-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                            >
                                {label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 ml-auto">
                        {showCart && (
                            <Button
                                data-testid="navbar-cart-button"
                                onClick={() => cart.setOpen(true)}
                                variant="ghost"
                                size="icon"
                                className="relative"
                                aria-label={
                                    cart.count > 0
                                        ? `Carrito, ${cart.count} artículos`
                                        : "Carrito"
                                }
                            >
                                <ShoppingBag className="w-5 h-5" aria-hidden="true" />
                                {cart.count > 0 && (
                                    <motion.span
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        data-testid="navbar-cart-count"
                                        aria-hidden="true"
                                        className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 grid place-items-center text-[11px] font-semibold text-primary-foreground bg-primary rounded-full"
                                    >
                                        {cart.count}
                                    </motion.span>
                                )}
                            </Button>
                        )}

                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="navbar-user-menu"
                                        className="hidden sm:flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-zinc-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                                    >
                                        {user.picture ? (
                                            <img
                                                src={user.picture}
                                                alt=""
                                                className="w-7 h-7 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-7 h-7 rounded-full bg-primary-subtle grid place-items-center text-xs font-semibold text-primary-hover">
                                                {(user.name || user.email)[0]?.toUpperCase()}
                                            </div>
                                        )}
                                        <span className="text-sm font-medium text-[#1A1A1A] max-w-[120px] truncate">
                                            {user.name || user.email}
                                        </span>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel className="text-xs font-normal text-zinc-500">
                                        Sesión iniciada como
                                        <div className="text-sm font-medium text-zinc-900 truncate">
                                            {user.email}
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {user.role === "admin" && (
                                        <DropdownMenuItem
                                            data-testid="navbar-menu-admin"
                                            onClick={() => navigate("/admin")}
                                        >
                                            <Shield className="w-4 h-4 mr-2" />
                                            Panel admin
                                        </DropdownMenuItem>
                                    )}
                                    {(user.role === "merchant" || user.role === "customer") && (
                                        <DropdownMenuItem
                                            data-testid="navbar-menu-dashboard"
                                            onClick={() => navigate("/dashboard")}
                                        >
                                            <Store className="w-4 h-4 mr-2" />
                                            Mi tienda
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                        data-testid="navbar-menu-account"
                                        onClick={() => navigate("/cuenta")}
                                    >
                                        <UserIcon className="w-4 h-4 mr-2" />
                                        Mi cuenta
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        data-testid="navbar-menu-logout"
                                        onClick={logout}
                                        className="text-red-600 focus:text-red-700"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Cerrar sesión
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Button
                                    data-testid="navbar-login-button"
                                    variant="ghost"
                                    className="hidden sm:inline-flex font-medium text-[#1A1A1A]"
                                    asChild
                                >
                                    <Link to="/login">Iniciar sesión</Link>
                                </Button>
                                <Button
                                    data-testid="navbar-cta-create-store"
                                    className="hidden sm:inline-flex h-11 px-6 rounded-full font-semibold bg-primary hover:bg-primary-hover text-primary-foreground"
                                    asChild
                                >
                                    <Link to="/registro?rol=merchant">
                                        Crear tienda gratis
                                    </Link>
                                </Button>
                            </>
                        )}

                        <Button
                            data-testid="navbar-mobile-toggle"
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setMobileOpen(!mobileOpen)}
                            aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
                            aria-expanded={mobileOpen}
                            aria-controls="mobile-nav"
                        >
                            {mobileOpen ? (
                                <X className="w-5 h-5" aria-hidden="true" />
                            ) : (
                                <Menu className="w-5 h-5" aria-hidden="true" />
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Menú móvil */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        id="mobile-nav"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="lg:hidden border-t border-zinc-200 overflow-hidden bg-white"
                    >
                        <div className="px-4 py-3 flex flex-col gap-1">
                            {SECTIONS.map(([id, label]) => (
                                <a
                                    key={id}
                                    href={`/#${id}`}
                                    onClick={goToSection(id)}
                                    className="px-3 py-3 rounded-md hover:bg-zinc-50 text-[15px] font-medium text-[#1A1A1A]"
                                >
                                    {label}
                                </a>
                            ))}
                            <div className="h-px bg-zinc-200 my-2" />
                            {isAuthenticated ? (
                                <>
                                    {user.role === "admin" && (
                                        <Link
                                            to="/admin"
                                            onClick={() => setMobileOpen(false)}
                                            className="px-3 py-3 rounded-md hover:bg-zinc-50 text-[15px] font-medium"
                                        >
                                            Panel admin
                                        </Link>
                                    )}
                                    {(user.role === "merchant" || user.role === "customer") && (
                                        <Link
                                            to="/dashboard"
                                            onClick={() => setMobileOpen(false)}
                                            className="px-3 py-3 rounded-md hover:bg-zinc-50 text-[15px] font-medium"
                                        >
                                            Mi tienda
                                        </Link>
                                    )}
                                    <Link
                                        to="/cuenta"
                                        onClick={() => setMobileOpen(false)}
                                        className="px-3 py-3 rounded-md hover:bg-zinc-50 text-[15px] font-medium"
                                    >
                                        Mi cuenta
                                    </Link>
                                    <button
                                        onClick={() => {
                                            logout();
                                            setMobileOpen(false);
                                        }}
                                        className="px-3 py-3 rounded-md hover:bg-zinc-50 text-[15px] font-medium text-red-600 text-left"
                                    >
                                        Cerrar sesión
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        onClick={() => setMobileOpen(false)}
                                        className="px-3 py-3 rounded-md hover:bg-zinc-50 text-[15px] font-medium"
                                    >
                                        Iniciar sesión
                                    </Link>
                                    <Button
                                        data-testid="mobile-cta-create-store"
                                        className="h-12 rounded-full font-semibold bg-primary hover:bg-primary-hover text-primary-foreground mt-2 w-full"
                                        asChild
                                    >
                                        <Link
                                            to="/registro?rol=merchant"
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            Crear tienda gratis
                                        </Link>
                                    </Button>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
